import { Injectable } from '@angular/core';
import { ProductV2, StoreVarietyV2 } from '../../shared/models/products-v2.model';
import { VarietyAvailability } from '../../shared/models/payment.model';
import { Exchange } from '../../shared/models/exchange.model';
import { CurrencyDetail } from '../../shared/models/products.model';

// Type helper for currency that may or may not have 'name'
type CurrencyInput = CurrencyDetail | Omit<CurrencyDetail, 'name'> & { name?: string };
import {
  PriceSuggestionsData,
  PricingType,
  ExchangeInfo,
  FixedPriceOption,
  RangedPriceConfig,
  PriceSelection,
  StockStatus,
  ProcessedVariety,
  PriceSuggestionItem,
  StockInfo
} from '../models/price-suggestions-data.model';

@Injectable({ providedIn: 'root' })
export class PriceSuggestionsService {
  
  /**
   * Main entry point - calculates all pricing data for SSR
   * This is called from the resolver before component renders
   */
  calculatePriceSuggestions(params: {
    product: ProductV2;
    stockAvailability: VarietyAvailability[];
    exchangeRates: Exchange[];
    storeCurrency: CurrencyDetail;
    fillQueryParam: number | null;
  }): PriceSuggestionsData {
    const { product, stockAvailability, exchangeRates, storeCurrency, fillQueryParam } = params;
    
    // Step 1: Resolve exchange rate
    const exchange = this.resolveExchangeRate(
      product.currency_detail,
      storeCurrency,
      exchangeRates
    );
    
    // Step 2: Process varieties with exchange applied
    const processedVarieties = this.processVarieties(
      product.product_detail.varieties,
      exchange
    );
    
    // Step 3: Determine pricing type
    const pricingType = this.determinePricingType(
      processedVarieties,
      stockAvailability
    );
    
    // Step 4: Build price options based on type
    let fixedPriceOptions: FixedPriceOption[] | undefined;
    let rangedPriceConfig: RangedPriceConfig | undefined;
    
    if (pricingType === PricingType.FIXED) {
      fixedPriceOptions = this.buildFixedPriceOptions(
        processedVarieties,
        stockAvailability,
        exchange
      );
    } else {
      rangedPriceConfig = this.buildRangedPriceConfig(
        processedVarieties,
        stockAvailability,
        exchange
      );
    }
    
    // Step 5: Determine default selection
    const defaultSelection = this.getDefaultSelection({
      pricingType,
      fixedPriceOptions,
      rangedPriceConfig,
      fillQueryParam
    });
    
    // Step 6: Calculate stock status
    const stockStatus = this.calculateStockStatus({
      pricingType,
      fixedPriceOptions,
      rangedPriceConfig
    });
    
    return {
      pricingType,
      exchange,
      fixedPriceOptions,
      rangedPriceConfig,
      defaultSelection,
      stockStatus,
      processedVarieties
    };
  }
  
  /**
   * Product → USD rate and labels (used for payable amounts, processed varieties, checkout).
   */
  resolveExchangeRate(
    productCurrency: CurrencyInput,
    storeCurrency: CurrencyDetail,
    exchangeRates: Exchange[]
  ): ExchangeInfo {
    const fromCurrency = {
      code: productCurrency.code,
      symbol: productCurrency.symbol
    };

    let productToStore = 1;
    if (productCurrency.iso_code !== storeCurrency.iso_code) {
      const row = exchangeRates.find(
        r => r.from_currency.code === productCurrency.code &&
             r.to_currency.code === storeCurrency.code
      );
      if (row) {
        productToStore = parseFloat(row.rate);
      }
    }

    const usd = this.resolveProductToUsdRate(
      productCurrency,
      storeCurrency,
      productToStore,
      exchangeRates
    );

    return {
      rate: usd.rate,
      fromCurrency,
      toCurrency: usd.currency,
      hasExchange: usd.rate !== 1 || !this.isUsdCurrency(productCurrency)
    };
  }

  private resolveProductToUsdRate(
    productCurrency: CurrencyInput,
    storeCurrency: CurrencyDetail,
    productToStoreRate: number,
    exchangeRates: Exchange[]
  ): { rate: number; currency: { code: string; symbol: string } } {
    const defaultUsd = { code: 'USD', symbol: '$' };

    if (this.isUsdCurrency(productCurrency)) {
      return {
        rate: 1,
        currency: {
          code: productCurrency.code,
          symbol: productCurrency.symbol || defaultUsd.symbol
        }
      };
    }

    const directToUsd = this.findCurrencyMultiplierToUsd(productCurrency, exchangeRates);
    if (directToUsd != null) {
      return {
        rate: directToUsd.rate,
        currency: directToUsd.currency
      };
    }

    const storeToUsd = this.findCurrencyMultiplierToUsd(storeCurrency, exchangeRates);
    if (storeToUsd != null) {
      return {
        rate: productToStoreRate * storeToUsd.rate,
        currency: defaultUsd
      };
    }

    if (this.isUsdCurrency(storeCurrency)) {
      return {
        rate: productToStoreRate,
        currency: {
          code: storeCurrency.code,
          symbol: storeCurrency.symbol || defaultUsd.symbol
        }
      };
    }

    return {
      rate: productToStoreRate,
      currency: defaultUsd
    };
  }

  private isUsdCurrency(c: CurrencyInput | CurrencyDetail): boolean {
    return c.iso_code === 'USD' || c.code === 'USD';
  }

  /**
   * How many USD one unit of `fromCurrency` is worth, using direct or inverse rows from the API.
   */
  private findCurrencyMultiplierToUsd(
    fromCurrency: CurrencyInput,
    exchangeRates: Exchange[]
  ): { rate: number; currency: { code: string; symbol: string } } | null {
    const defaultUsd = { code: 'USD', symbol: '$' };
    const fromMatches = (from: { code: string; iso_code: string }) =>
      from.code === fromCurrency.code || from.iso_code === fromCurrency.iso_code;

    const direct = exchangeRates.find(
      r => fromMatches(r.from_currency) && this.isUsdCurrency(r.to_currency)
    );
    if (direct) {
      const rate = parseFloat(direct.rate);
      if (Number.isFinite(rate)) {
        return {
          rate,
          currency: {
            code: direct.to_currency.code,
            symbol: direct.to_currency.symbol || defaultUsd.symbol
          }
        };
      }
    }

    const inverse = exchangeRates.find(
      r => this.isUsdCurrency(r.from_currency) && fromMatches(r.to_currency)
    );
    if (inverse) {
      const rateUsdPerTo = parseFloat(inverse.rate);
      if (Number.isFinite(rateUsdPerTo) && rateUsdPerTo > 0) {
        return {
          rate: 1 / rateUsdPerTo,
          currency: {
            code: inverse.from_currency.code,
            symbol: inverse.from_currency.symbol || defaultUsd.symbol
          }
        };
      }
    }

    return null;
  }
  
  /**
   * Process varieties with exchange rate applied
   */
  processVarieties(
    varieties: StoreVarietyV2[],
    exchange: ExchangeInfo
  ): ProcessedVariety[] {
    return varieties.map(v => ({
      id: v?.variety_detail?.id,
      sku: v?.variety_detail?.sku,
      title: v?.variety_detail?.title,
      minPrice: +v?.variety_detail?.min_price * exchange.rate,
      maxPrice: +v?.variety_detail?.max_price * exchange.rate,
      originalMinPrice: v?.variety_detail?.min_price,
      originalMaxPrice: v?.variety_detail?.max_price,
      currency: exchange.toCurrency.symbol,
      priceStep: v?.price_step || 1,
      suggestions: v?.suggestions || [],
      activationFee: 0 // Will be populated from variety if available
    }));
  }
  
  /**
   * Determine if product is fixed or ranged price
   * 
   * Logic:
   * - If has fixed varieties AND ranged varieties:
   *   → Prefer ranged if its SKU is available
   *   → Otherwise use fixed
   * - If only fixed → FIXED
   * - If only ranged → RANGED
   */
  determinePricingType(
    varieties: ProcessedVariety[],
    availability: VarietyAvailability[]
  ): PricingType {
    const hasFixed = varieties.some(v => v.minPrice === v.maxPrice);
    const hasRanged = varieties.some(v => v.minPrice !== v.maxPrice && v.suggestions.length > 0);
    
    if (hasFixed && hasRanged) {
      // Mixed: prefer ranged if available
      const rangedVariety = varieties.find(v => v.minPrice !== v.maxPrice);
      if (rangedVariety) {
        const isRangedAvailable = availability.some(a => 
          a.sku === rangedVariety.sku && 
          (a.super_immediate || a.pre_order || (a.prices && a.prices.length > 0))
        );
        return isRangedAvailable ? PricingType.RANGED : PricingType.FIXED;
      }
      return PricingType.FIXED;
    }
    
    return hasFixed ? PricingType.FIXED : PricingType.RANGED;
  }
  
  /**
   * Build fixed price options with stock info
   */
  buildFixedPriceOptions(
    varieties: ProcessedVariety[],
    availability: VarietyAvailability[],
    exchange: ExchangeInfo
  ): FixedPriceOption[] {
    return varieties
      .filter(v => v.minPrice === v.maxPrice)  // Only fixed price varieties
      .map(v => {
        const skuAvailability = availability.find(a => a.sku === v.sku);
        const stockInfo = this.getSkuStockInfo(skuAvailability);
        
        return {
          label: `${exchange.toCurrency.symbol}${this.formatPrice(v.minPrice)}`,
          value: v.minPrice,
          originalValue: v.originalMinPrice,
          sku: v.sku,
          title: v.title,
          outOfStock: !stockInfo.isAvailable,
          stockInfo
        };
      });
  }
  
  /**
   * Build ranged price configuration
   */
  buildRangedPriceConfig(
    varieties: ProcessedVariety[],
    availability: VarietyAvailability[],
    exchange: ExchangeInfo
  ): RangedPriceConfig {
    const rangedVariety = varieties.find(v => v.minPrice !== v.maxPrice);
    
    if (!rangedVariety) {
      throw new Error('No ranged variety found');
    }
    
    const skuAvailability = availability.find(a => a.sku === rangedVariety.sku);
    const stockInfo = this.getSkuStockInfo(skuAvailability);
    
    // Determine if custom amount is allowed
    const customAmountAllowed = skuAvailability?.super_immediate === true || 
                                 skuAvailability?.pre_order === true;
    
    // Build suggestions with stock info
    const suggestions = this.buildRangedSuggestions(
      rangedVariety.suggestions,
      skuAvailability,
      exchange
    );
    
    return {
      minPrice: rangedVariety.minPrice,
      maxPrice: rangedVariety.maxPrice,
      originalMinPrice: rangedVariety.originalMinPrice,
      originalMaxPrice: rangedVariety.originalMaxPrice,
      priceStep: rangedVariety.priceStep,
      customAmountAllowed,
      suggestions,
      sku: rangedVariety.sku
    };
  }
  
  /**
   * Get default selection based on query param or first available
   */
  getDefaultSelection(params: {
    pricingType: PricingType;
    fixedPriceOptions?: FixedPriceOption[];
    rangedPriceConfig?: RangedPriceConfig;
    fillQueryParam: number | null;
  }): PriceSelection {
    const { pricingType, fixedPriceOptions, rangedPriceConfig, fillQueryParam } = params;
    
    if (pricingType === PricingType.FIXED && fixedPriceOptions) {
      return this.getFixedDefaultSelection(fixedPriceOptions, fillQueryParam);
    }
    
    if (rangedPriceConfig) {
      return this.getRangedDefaultSelection(rangedPriceConfig, fillQueryParam);
    }
    
    // Fallback
    return {
      type: 'FIXED',
      price: 0,
      sku: '',
      isValid: false
    };
  }
  
  private getFixedDefaultSelection(
    options: FixedPriceOption[],
    fillParam: number | null
  ): PriceSelection {
    // Try to find fill param match
    if (fillParam !== null) {
      const matchIndex = options.findIndex(o => 
        o.value === fillParam && !o.outOfStock
      );
      if (matchIndex !== -1) {
        return {
          type: 'FIXED',
          price: options[matchIndex].value,
          sku: options[matchIndex].sku,
          isValid: true,
          selectedIndex: matchIndex
        };
      }
    }
    
    // Fall back to first in-stock
    const firstInStockIndex = options.findIndex(o => !o.outOfStock);
    if (firstInStockIndex !== -1) {
      return {
        type: 'FIXED',
        price: options[firstInStockIndex].value,
        sku: options[firstInStockIndex].sku,
        isValid: true,
        selectedIndex: firstInStockIndex
      };
    }
    
    // All out of stock - select first anyway
    return {
      type: 'FIXED',
      price: options[0]?.value ?? 0,
      sku: options[0]?.sku ?? '',
      isValid: false,
      selectedIndex: 0
    };
  }
  
  private getRangedDefaultSelection(
    config: RangedPriceConfig,
    fillParam: number | null
  ): PriceSelection {
    // UI order when customAmountAllowed: [custom at 0, suggestion0 at 1, suggestion1 at 2, ...]
    const customUiIndex = 0;
    const suggestionIndexToUiIndex = (suggestionIndex: number) =>
      config.customAmountAllowed ? suggestionIndex + 1 : suggestionIndex;

    // Try to find fill param in suggestions
    if (fillParam !== null) {
      const suggestionIndex = config.suggestions.findIndex(s => s.value === fillParam);
      if (suggestionIndex !== -1) {
        return {
          type: 'RANGED_SUGGESTION',
          price: config.suggestions[suggestionIndex].value,
          sku: config.sku,
          isValid: true,
          selectedIndex: suggestionIndexToUiIndex(suggestionIndex)
        };
      }
      
      // Check if custom amount is allowed and within range (fill param in original currency)
      const origMin = parseFloat(config.originalMinPrice);
      const origMax = parseFloat(config.originalMaxPrice);
      if (config.customAmountAllowed &&
          !Number.isNaN(origMin) && !Number.isNaN(origMax) &&
          fillParam >= origMin &&
          fillParam <= origMax) {
        return {
          type: 'RANGED_CUSTOM',
          price: fillParam,
          sku: config.sku,
          isValid: true,
          selectedIndex: customUiIndex
        };
      }
    }
    
    // Fall back to first suggestion
    if (config.suggestions.length > 0) {
      return {
        type: 'RANGED_SUGGESTION',
        price: config.suggestions[0].value,
        sku: config.sku,
        isValid: true,
        selectedIndex: suggestionIndexToUiIndex(0)
      };
    }
    
    // Fallback to custom if allowed – leave input empty so user enters their own value
    if (config.customAmountAllowed) {
      return {
        type: 'RANGED_CUSTOM',
        price: config.minPrice,
        sku: config.sku,
        isValid: false,
        selectedIndex: customUiIndex,
        customAmountEmpty: true
      };
    }
    
    // No options available
    return {
      type: 'RANGED_SUGGESTION',
      price: config.minPrice,
      sku: config.sku,
      isValid: false,
      selectedIndex: suggestionIndexToUiIndex(0)
    };
  }
  
  /**
   * Calculate overall stock status
   */
  calculateStockStatus(params: {
    pricingType: PricingType;
    fixedPriceOptions?: FixedPriceOption[];
    rangedPriceConfig?: RangedPriceConfig;
  }): StockStatus {
    const { pricingType, fixedPriceOptions, rangedPriceConfig } = params;
    
    if (pricingType === PricingType.FIXED && fixedPriceOptions) {
      const allOutOfStock = fixedPriceOptions.every(o => o.outOfStock);
      return {
        isFullyOutOfStock: allOutOfStock,
        hasSomeInStock: !allOutOfStock,
        fixedPriceOutOfStock: allOutOfStock
      };
    }
    
    if (rangedPriceConfig) {
      const isOutOfStock = !rangedPriceConfig.customAmountAllowed && 
                           rangedPriceConfig.suggestions.every(s => !s.inStock);
      return {
        isFullyOutOfStock: isOutOfStock,
        hasSomeInStock: !isOutOfStock,
        rangedPriceOutOfStock: isOutOfStock
      };
    }
    
    return {
      isFullyOutOfStock: true,
      hasSomeInStock: false
    };
  }
  
  // ========================================
  // PRIVATE HELPERS
  // ========================================
  
  private getSkuStockInfo(availability: VarietyAvailability | undefined): StockInfo {
    if (!availability) {
      return { isAvailable: false };
    }
    
    if (availability.super_immediate) {
      return { isAvailable: true, mode: 'IMMEDIATE' };
    }
    
    if (availability.pre_order) {
      return { isAvailable: true, mode: 'PRE_ORDER' };
    }
    
    if (availability.prices && availability.prices.length > 0) {
      return { isAvailable: true, mode: 'LIMITED_PRICES' };
    }
    
    return { isAvailable: false };
  }
  
  private buildRangedSuggestions(
    suggestions: number[],
    availability: VarietyAvailability | undefined,
    exchange: ExchangeInfo
  ): PriceSuggestionItem[] {
    // Get available prices from API if available
    const availablePrices = availability?.prices?.map(p => parseFloat(p.face_value)) ?? [];
    
    // Determine which prices to show based on availability flags
    let pricesToShow: number[];
    
    if (availability?.super_immediate === true) {
      pricesToShow = [...suggestions].sort((a, b) => a - b);
    } else if (availability?.pre_order === true) {
      pricesToShow = [...suggestions].sort((a, b) => a - b);
    } else if (availablePrices.length > 0) {
      pricesToShow = availablePrices.sort((a, b) => a - b);
    } else {
      pricesToShow = [...suggestions].sort((a, b) => a - b);
    }
    
    return pricesToShow.map(value => ({
      label: `${exchange.fromCurrency.symbol}${this.formatPrice(value)}`,
      value,
      inStock: availability?.super_immediate === true ||
               availability?.pre_order === true ||
               availablePrices.includes(value)
    }));
  }
  
  private formatPrice(price: number): string {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }
}
